import {
  ANCHOR_OFFSET_FROM_STICKY_HEADER,
  BrandPageFollowButton,
  InteractiveProductTile,
  LoadMoreComponentDisplayConfig,
  ProductMiniAdpWithBasketFlyout,
  STREAM_TOP_SPACING,
  SizeSelectionOpenedStateKey,
  StoryHeader,
  StoryInlineFilter,
  StoryMeta,
  StoryNavigationTree,
  StoryProductStream,
  StoryStreamHeader,
  useMiniAdpDialogOpenedState,
  useProductStreamDisplayState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor} from '@aboutyou/router';
import {SplitViewGrid, Z_INDEXES} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {StrictMode, Suspense, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import {useADPPreload} from '../ArticleDetailPage/useADPPreload';

const loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 10000,
  tilesBeforeConsecutivelyDisplay: 10000,
};

export const StoryPage: React.FC = () => {
  const {productImageType, updateProductImageType} =
    useProductStreamDisplayState();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState();

  const inlineFiltersRef = useRef<HTMLDivElement | null>(null);

  useADPPreload();

  return (
    <StrictMode>
      <StoryMeta />

      <StoryHeader
        HeaderElements={({id, brandName, promotionTracker}) => (
          <>
            <BrandPageFollowButton
              brand={{
                id,
                name: brandName,
                path: promotionTracker.destination ?? '',
              }}
              promotionTracker={promotionTracker}
            />
          </>
        )}
      />

      <StyledSplitViewWithCollapsibleNav>
        <ScrollTopAnchor
          defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
          as={SplitViewGrid}
          split={'4:8'}
          splitLarge={'2:10'}
          refStart={sidebarRef}
          contentStart={
            <Suspense>
              <Sticky
                overflowScroll="flow"
                experimentalNative
                container={sidebarRef}
                style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
                defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
              >
                <StyledStoryNavigationTree />
              </Sticky>
            </Suspense>
          }
          contentEnd={
            <ContentSection>
              <StoryStreamHeader
                productImageType={productImageType}
                onChangeProductImageType={updateProductImageType}
                inlineFiltersRef={inlineFiltersRef}
              />
              <StoryInlineFilter inlineFiltersRef={inlineFiltersRef} />
              <Suspense>
                <StoryProductStream
                  gridColumns={undefined}
                  constrainStoryTilesToViewportBoundary
                  loadMoreComponentDisplayConfig={
                    loadMoreComponentDisplayConfig
                  }
                  interactiveHoverMode
                  onClickAddCta={openProductMiniADPDialog}
                  ProductTileComponent={InteractiveProductTile}
                />
              </Suspense>
            </ContentSection>
          }
        />
      </StyledSplitViewWithCollapsibleNav>

      <ProductMiniAdpWithBasketFlyout
        showAddToWishlistButton
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
      />
    </StrictMode>
  );
};

const ContentSection = styled.section`
  padding-top: ${STREAM_TOP_SPACING}px;
`;

const StyledStoryNavigationTree = styled(StoryNavigationTree)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;

// StoryNavigationTree renders a data-story-navigation-tree-empty marker
// instead of its tree when the story has no categories to navigate -
// when that marker is present, the reserved sidebar column width is
// collapsed and given back to the product stream.
const StyledSplitViewWithCollapsibleNav = styled.div`
  &:has([data-story-navigation-tree-empty]) {
    [data-testid='splitView'] {
      gap: 0;
    }

    [data-testid='sectionStart'] {
      width: 0;
    }

    [data-testid='sectionEnd'] {
      width: 100%;
    }
  }
`;
